import React, { useState, useEffect } from "react";
import {
  addNewDoc,
  getAllDocsSnap,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../../utils/firebase/Firebase.utils";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import Select from "react-select";
import PageModal from "../Components/PageModal";
import {
  FiCalendar,
  FiClock,
  FiHome,
  FiUsers,
  FiNavigation2,
  FiMail,
  FiUpload,
  FiX,
  FiPlus,
  FiCheck,
} from "react-icons/fi";
import { adminEmailAddress } from "../../../utils/constants";

function AddGuestModal({ setAddGuestModalOpen }) {
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [formData, setFormData] = useState({
    linkedIdentifiers: [],
    date: "",
    time: "",
  });
  const [guestsArray, setGuestsArray] = useState([{}]); // Initialize with one guest object
  const [inProcess, setInProcess] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [uploadingInProgress, setUploadingInProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeFileUpload, setActiveFileUpload] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch real unit identifiers from the residents collection
  useEffect(() => {
    const fetchResidents = async () => {
      await getAllDocsSnap("residents", (data) => {
        const options = data
          .map((resident) => ({
            value: resident.identifier,
            label: `${resident.owner1 || "Unknown"} (${resident.identifier})`,
          }))
          .filter((option) => option.value); // Filter out any undefined identifiers

        setUnitOptions(options);
        setIsLoading(false);
      });
    };

    fetchResidents();
  }, []);

  const addGuest = () => {
    setNumberOfGuests((prev) => prev + 1);
    setGuestsArray((prev) => [...prev, {}]); // Append a new guest object
  };

  const handleChange = (e, index) => {
    const updatedGuests = guestsArray.map((guest, idx) => {
      if (idx === index) {
        return { ...guest, [e.target.name]: e.target.value };
      }
      return guest;
    });
    setGuestsArray(updatedGuests);
  };

  const handleUpload = async (e, index) => {
    const file = e.target.files[0];
    const storageRef = ref(storage, `guests/${file.name} + ${v4()}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
        setUploadingInProgress(true);
        setActiveFileUpload(index);
      },
      (error) => {},
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          const updatedGuests = guestsArray.map((guest, idx) => {
            if (idx === index) {
              return { ...guest, identification: downloadURL };
            }
            return guest;
          });
          setGuestsArray(updatedGuests);
          setUploadingInProgress(false);
          setActiveFileUpload(null);
        });
      }
    );
  };

  const handleViewIdentification = (photo) => {
    setSelectedId(photo);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInProcess(true);

    const { date, time, linkedIdentifiers } = formData;

    if (!date || !time) {
      toast.error("Please fill all fields");
      setInProcess(false);
      return;
    }

    if (guestsArray.some((guest) => !guest.name || !guest.email)) {
      toast.error("Please fill all guest fields");
      setInProcess(false);
      return;
    }

    if (guestsArray.some((guest) => !guest.email.includes("@"))) {
      toast.error("Please enter a valid email address");
      setInProcess(false);
      return;
    }

    if (guestsArray.length < 1) {
      toast.error("Please add at least one guest");
      setInProcess(false);
      return;
    }

    if (linkedIdentifiers.length < 1) {
      toast.error("Please select a unit to be visited");
      setInProcess(false);
      return;
    }

    const dataObject = {
      date,
      time,
      guests: guestsArray,
      linkedIdentifiers,
      requestorName: "Admin", // For admin created passes
      requestedBy: adminEmailAddress,
      user: adminEmailAddress,
      status: "approved", // Auto-approve when created by admin
      requestDate: new Date().toISOString(),
    };

    try {
      // Add the new guest document and get the ID
      const docRef = await addNewDoc("guests", dataObject);
      toast.success("Guest Registration Successful");

      // Close modal and reset form
      setAddGuestModalOpen(false);
    } catch (error) {
      toast.error("Failed to add guest pass: " + error.message);
    } finally {
      setInProcess(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl p-6 max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-eleghoBlue">
            Add New Guest Pass
          </h2>
          <button
            onClick={() => setAddGuestModalOpen(false)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-eleghoBlue"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 max-w-4xl max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-eleghoBlue">
          Add New Guest Pass
        </h2>
        <button
          onClick={() => setAddGuestModalOpen(false)}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date and Time Selection */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FiCalendar className="w-4 h-4 text-eleghoBlue" />
                Visit Date
              </label>
              <input
                type="date"
                required
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                value={formData.date}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-eleghoBlue/20 focus:border-eleghoBlue"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FiClock className="w-4 h-4 text-eleghoBlue" />
                Visit Time
              </label>
              <input
                type="time"
                required
                onChange={(e) =>
                  setFormData({ ...formData, time: e.target.value })
                }
                value={formData.time}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-eleghoBlue/20 focus:border-eleghoBlue"
              />
            </div>
          </div>

          {/* Unit Selection */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FiHome className="w-4 h-4 text-eleghoBlue" />
              Units to Visit
            </label>
            <Select
              options={unitOptions}
              isMulti
              placeholder="Select units to visit"
              required
              onChange={(value) =>
                setFormData({
                  ...formData,
                  linkedIdentifiers: value.map((val) => val.value),
                })
              }
              value={formData.linkedIdentifiers.map((identifier) => ({
                value: identifier,
                label:
                  unitOptions.find((opt) => opt.value === identifier)?.label ||
                  identifier,
              }))}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {/* Guest Information */}
        <div className="space-y-4">
          {Array.from({ length: numberOfGuests }).map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-xl p-6 relative"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-eleghoBlue">
                  Guest {index + 1}
                </h3>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setNumberOfGuests(numberOfGuests - 1);
                      setGuestsArray(
                        guestsArray.filter((_, idx) => idx !== index)
                      );
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FiUsers className="w-4 h-4 text-eleghoBlue" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    onChange={(e) => handleChange(e, index)}
                    value={guestsArray[index]?.name || ""}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-eleghoBlue/20 focus:border-eleghoBlue"
                    placeholder="Enter guest's full name"
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FiNavigation2 className="w-4 h-4 text-eleghoBlue" />
                    Vehicle Plate Number
                  </label>
                  <input
                    type="text"
                    name="plateNumber"
                    onChange={(e) => handleChange(e, index)}
                    value={guestsArray[index]?.plateNumber || ""}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-eleghoBlue/20 focus:border-eleghoBlue"
                    placeholder="Enter vehicle plate number"
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FiMail className="w-4 h-4 text-eleghoBlue" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    onChange={(e) => handleChange(e, index)}
                    value={guestsArray[index]?.email || ""}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-eleghoBlue/20 focus:border-eleghoBlue"
                    placeholder="Enter guest's email address"
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FiUpload className="w-4 h-4 text-eleghoBlue" />
                    Upload ID
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      onChange={(e) => handleUpload(e, index)}
                      accept="image/*"
                      className="hidden"
                      id={`file-upload-${index}`}
                    />
                    <label
                      htmlFor={`file-upload-${index}`}
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-center text-sm text-gray-600"
                    >
                      {guestsArray[index]?.identification
                        ? "Change ID Photo"
                        : "Upload ID Photo"}
                    </label>
                    {guestsArray[index]?.identification && (
                      <button
                        type="button"
                        onClick={() =>
                          handleViewIdentification(
                            guestsArray[index].identification
                          )
                        }
                        className="px-4 py-2 bg-eleghoBlue/10 text-eleghoBlue rounded-lg hover:bg-eleghoBlue/20 transition-colors"
                      >
                        View
                      </button>
                    )}
                  </div>
                  {uploadingInProgress && activeFileUpload === index && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-eleghoBlue h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          {numberOfGuests < 12 && (
            <motion.button
              type="button"
              onClick={addGuest}
              className="w-full py-3 bg-eleghoGreen/10 text-eleghoGreen rounded-lg hover:bg-eleghoGreen/20 transition-colors flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiPlus className="w-5 h-5" />
              Add Another Guest
            </motion.button>
          )}
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={inProcess}
          className="w-full py-3 bg-eleghoBlue text-white rounded-lg hover:bg-eleghoBlue/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {inProcess ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <FiCheck className="w-5 h-5" />
              Add Guest Pass
            </>
          )}
        </motion.button>
      </form>

      {selectedId && (
        <PageModal>
          <div className="page relative">
            <button
              className="close-button"
              onClick={() => setSelectedId(null)}
            >
              Close
            </button>
            <img
              src={selectedId}
              alt="Guest ID"
              className="max-w-full h-auto rounded-lg"
            />
          </div>
        </PageModal>
      )}
    </div>
  );
}

export default AddGuestModal;
